/* eslint-disable no-undef */
import React, { createContext, useState, useEffect } from "react";
import keycloak from "../utils/keycloakAuth";
import { LoadingBackground } from "../views/LoginLayout/styled";
import { getRealm } from "../utils/common";
import Loading from "../components/Loading";
import useSessionStorage from "../hooks/useSessionStorage";
import appInsights from "../utils/applicationInsights";

export const useAuth = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useSessionStorage("__keycloak_token__");
  const [parsedToken, setParsedToken] = useSessionStorage(
    "__keycloak_token_parsed__"
  );

  const setTokenInSessionStorage = keycloak => {
    setToken(keycloak.token);
    setParsedToken(keycloak.tokenParsed);
  };

  const updateToken = () => {
    keycloak
      .updateToken(60)
      .then(refreshed => {
        if (refreshed) {
          setTokenInSessionStorage(keycloak);
        }
      })
      .catch(e => {
        setError(e);
        console.error("Failed to refresh token");
      });
  };

  useEffect(() => {
    const realmRegExp = new RegExp(`/${getRealm()}/`, "i");
    keycloak
      .init({
        onLoad: "login-required",
        silentCheckSsoRedirectUri:
          window.location.origin + "/silent-check-sso.html",
        redirectUri: `${window.location.href.replace(
          realmRegExp,
          `/${getRealm()}/`
        )}`,
      })
      .then(auth => {
        if (!auth) {
          // Try again to auth!
          window.location.reload();
        } else {
          const userId = keycloak.tokenParsed.sub;
          const email = keycloak.tokenParsed.email;

          // Successful here is the token!
          setTokenInSessionStorage(keycloak);
          if (
            window.__insp &&
            process.env.REACT_APP_LOG_INSPECTLET === "true"
          ) {
            window.__insp.push(["identify", email]);
            window.__insp.push([
              "tagSession",
              {
                realm: keycloak.realm,
                practice: keycloak.practice,
              },
            ]);
          }

          appInsights.clearAuthenticatedUserContext();

          appInsights.setAuthenticatedUserContext(email, userId, true);
        }
      })
      .catch(e => {
        console.error(e);
        setError(e);
        window.location.reload();
      })
      .finally(() => {
        setLoading(false);
      });

    // Periodically Refresh Token
    setInterval(() => {
      updateToken();
    }, 300000);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { loading, error, token, parsedToken, updateToken };
};

export const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const { loading, error, token, parsedToken, updateToken } = useAuth();

  return (
    <AuthContext.Provider
      value={{
        loading,
        error,
        token,
        parsedToken,
        updateToken,
      }}
    >
      {loading && (
        <LoadingBackground>
          <Loading subtext="" color="black" />
        </LoadingBackground>
      )}
      {error && (
        <LoadingBackground>
          <Loading subtext={error} color="error" />
        </LoadingBackground>
      )}
      {!loading && !error && children}
    </AuthContext.Provider>
  );
};
